import React from "react";
import { Link } from "react-router-dom";
import { CircleMarker, Popup } from "react-leaflet";
import {
  Phone,
  Calendar,
  BellRing,
  Plus,
  Check,
  ExternalLink,
} from "lucide-react";
import { ConfidenceBadge } from "../ConfidenceBadge";
import {
  CONFIDENCE_COLORS,
  ALERT_COLOR,
  SELECTED_COLOR,
  displayName,
} from "./mapHelpers";

/**
 * A single dispensary point on the map. Colored by confidence tier, ringed
 * when selected for routing, and shows a rich popup with phone, opening
 * date, and an "Add to route" toggle.
 */
export function MarkerDot({ d, isSelected, onToggle }) {
  const isAlert = !!d.dcc_license_alert;
  const color = isAlert
    ? ALERT_COLOR
    : CONFIDENCE_COLORS[(d.confidence || "UNKNOWN").toUpperCase()] ||
      CONFIDENCE_COLORS.UNKNOWN;
  return (
    <CircleMarker
      center={[d.latitude, d.longitude]}
      radius={isSelected ? 10 : isAlert ? 8 : 6}
      pathOptions={{
        color: isSelected ? SELECTED_COLOR : "#0a0a0a",
        weight: isSelected ? 3 : 1.5,
        fillColor: color,
        fillOpacity: 0.92,
      }}
    >
      <Popup>
        <div
          data-testid={`map-popup-${d.id}`}
          className="text-zinc-100 min-w-[230px]"
        >
          <div className="text-sm font-semibold leading-tight">
            {displayName(d)}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5">
            {d.address ? `${d.address}, ` : ""}
            {d.city}
            {d.county ? ` · ${d.county} Co.` : ""}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <ConfidenceBadge value={d.confidence} />
            {isAlert && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-rose-300 bg-rose-400/10 border border-rose-400/20 px-1.5 py-0.5 rounded">
                <BellRing className="w-2.5 h-2.5" />
                Lic. alert
              </span>
            )}
          </div>
          {d.phone && (
            <a
              href={`tel:${d.phone.replace(/[^\d+]/g, "")}`}
              className="mt-2 inline-flex items-center gap-1.5 text-emerald-400 text-xs font-mono hover:underline"
            >
              <Phone className="w-3 h-3" />
              {d.phone}
            </a>
          )}
          {d.estimated_opening_date && (
            <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-mono text-zinc-300">
              <Calendar className="w-3 h-3 text-zinc-500" />
              {d.estimated_opening_date}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              data-testid={`popup-toggle-route-${d.id}`}
              type="button"
              onClick={() => onToggle(d)}
              className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded border transition-colors ${
                isSelected
                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25"
                  : "bg-zinc-800/60 border-zinc-700 text-zinc-200 hover:bg-zinc-700"
              }`}
            >
              {isSelected ? (
                <>
                  <Check className="w-3 h-3" />
                  Added to route
                </>
              ) : (
                <>
                  <Plus className="w-3 h-3" />
                  Add to route
                </>
              )}
            </button>
            <Link
              to={`/dispensaries/${d.id}`}
              className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 font-medium"
            >
              Details
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </Popup>
    </CircleMarker>
  );
}

export default MarkerDot;
